import { useCallback, useEffect, useRef, useState } from 'react';
import { DraftState, DraftStringInput, GroupEdits } from '../types';
import {
  applyStringEdit,
  clearDraft as clearPersistedDraft,
  createEmptyDraft,
  getDraft,
  saveDraft,
} from '../utils/draftStorage';

export interface UseDraftStateResult {
  /** Current draft, or null if no pending changes. */
  draftState: DraftState | null;
  /** True when any changes exist in the draft. */
  hasPendingChanges: boolean;
  /** True when the draft's baseVersion is behind the server version. */
  isStaleVersion: boolean;
  /** Add a new string to the draft. */
  addString: (s: DraftStringInput) => void;
  /** Edit an existing string (or update a newly added one in-place). */
  editString: (s: DraftStringInput) => void;
  /** Delete a string — removes from `added` if new, otherwise adds to `deleted`. */
  deleteString: (stringId: string) => void;
  /** Set group-level metadata edits. */
  editGroup: (edits: GroupEdits) => void;
  /** Clear all pending changes and remove the localStorage entry. */
  clearDraft: () => void;
}

function hasDraftChanges(draft: DraftState | null): boolean {
  if (!draft) return false;
  return (
    draft.added.length > 0 ||
    draft.edited.length > 0 ||
    draft.deleted.length > 0 ||
    draft.groupEdits !== undefined
  );
}

/**
 * Manages draft state for a string group, persisted to localStorage.
 * All mutations auto-persist; the hook initializes from localStorage on mount.
 */
export function useDraftState(
  namespaceId: string,
  stringGroupId: string,
  serverVersion: number,
): UseDraftStateResult {
  const [draft, setDraft] = useState<DraftState | null>(() =>
    getDraft(namespaceId, stringGroupId),
  );

  // Keep refs to avoid stale closures in callbacks
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Identity the persist effect last acted on. Used to detect the commit in
  // which navigation changed the group: on that commit `draft` still holds the
  // PREVIOUS group's value (the reload effect's setDraft only applies on the
  // next render), so persisting would write the old draft under the new key.
  const persistedKey = `${namespaceId}:${stringGroupId}`;
  const persistedKeyRef = useRef(persistedKey);

  // Re-load draft if the identity changes (navigation between groups)
  useEffect(() => {
    const loaded = getDraft(namespaceId, stringGroupId);
    setDraft(loaded);
  }, [namespaceId, stringGroupId]);

  // Auto-persist whenever draft changes. A draft that has been emptied (e.g.
  // its last added string was deleted) must clear the stored entry, otherwise
  // the stale changeset would be resurrected by getDraft on the next load.
  useEffect(() => {
    // On the commit where the group identity just changed, `draft` is stale
    // (belongs to the previous group). Skip this pass; the reload effect's
    // setDraft triggers a re-render that persists the correct draft.
    if (persistedKeyRef.current !== persistedKey) {
      persistedKeyRef.current = persistedKey;
      return;
    }
    if (!draft) return;
    if (hasDraftChanges(draft)) {
      saveDraft(namespaceId, stringGroupId, draft);
    } else {
      clearPersistedDraft(namespaceId, stringGroupId);
    }
  }, [draft, namespaceId, stringGroupId, persistedKey]);

  const ensureDraft = useCallback((): DraftState => {
    if (draftRef.current) return draftRef.current;
    return createEmptyDraft(serverVersion);
  }, [serverVersion]);

  const addString = useCallback(
    (s: DraftStringInput) => {
      setDraft((prev) => {
        const current = prev ?? ensureDraft();
        return { ...current, added: [...current.added, s] };
      });
    },
    [ensureDraft],
  );

  const editString = useCallback(
    (s: DraftStringInput) => {
      setDraft((prev) => applyStringEdit(prev ?? ensureDraft(), s));
    },
    [ensureDraft],
  );

  const deleteString = useCallback(
    (stringId: string) => {
      setDraft((prev) => {
        const current = prev ?? ensureDraft();
        // If in `added`, just remove — no need to track deletion of a never-saved string
        const addedIdx = current.added.findIndex(
          (a) => a.stringId === stringId,
        );
        if (addedIdx !== -1) {
          const updatedAdded = current.added.filter(
            (a) => a.stringId !== stringId,
          );
          return { ...current, added: updatedAdded };
        }
        // Remove from `edited` if present, then add to `deleted`
        const updatedEdited = current.edited.filter(
          (e) => e.stringId !== stringId,
        );
        const alreadyDeleted = current.deleted.includes(stringId);
        return {
          ...current,
          edited: updatedEdited,
          deleted: alreadyDeleted
            ? current.deleted
            : [...current.deleted, stringId],
        };
      });
    },
    [ensureDraft],
  );

  const editGroup = useCallback(
    (edits: GroupEdits) => {
      setDraft((prev) => {
        const current = prev ?? ensureDraft();
        // Merge rather than replace so independent group fields (e.g.
        // description vs cxIds) set through separate interactions accumulate
        // instead of the later call dropping the earlier one's values.
        return {
          ...current,
          groupEdits: { ...current.groupEdits, ...edits },
        };
      });
    },
    [ensureDraft],
  );

  const clearDraftAction = useCallback(() => {
    clearPersistedDraft(namespaceId, stringGroupId);
    setDraft(null);
  }, [namespaceId, stringGroupId]);

  const hasPendingChanges = hasDraftChanges(draft);
  const isStaleVersion = draft !== null && draft.baseVersion < serverVersion;

  return {
    draftState: draft,
    hasPendingChanges,
    isStaleVersion,
    addString,
    editString,
    deleteString,
    editGroup,
    clearDraft: clearDraftAction,
  };
}
